import React, { useState, useEffect } from "react";

import { useForm, SubmitHandler, set } from "react-hook-form";

import AddIcon from "@mui/icons-material/Add";

// common components
import TableHeader from "../../components/common/MagicTableHeader";
import PageHeader from "../../components/common/MagicPageHeader";
import Sidebar from "../../components/common/MagicDrawer_new";
import Table from "../../components/common/MagicTable";
import Modal from "../../components/common/Modal";
import Stack from "../../components/mui/Stack";

// components
import AddTargetForm from "../../components/configuration/forms/AddTargetForm";
import FilterTarget from "../../components/configuration/FilterTarget";
import {
  useCreateTargetMutation,
  useDeleteTargetMutation,
  useGetTargetParameterQuery,
  useGetTargetQuery,
} from "../../store/apis/configurationApis";
import {
  ManageAwardAftParser,
  targetAftParser,
} from "../../store/types/configuration";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import Button from "../../components/mui/Button";
import {
  useCenterListQuery,
  useUserListQuery,
} from "../../store/apis/centerApis";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { Label } from "@mui/icons-material";
import { showToast } from "../../helper/toaster";
import usePermission from "../../helper/usePermission";
import { acl } from "../../helper/userAccessConstant";
import ImageComponent from "../../components/mui/Image";
import Box from "../../components/mui/Box";
import Text from "../../components/mui/Text";

const Targets: React.FC = () => {
  const [isOpenAddTargetSidebar, setIsOpenAddTargetSidebar] =
    useState<boolean>(false);
  const [isOpenEditTargetSidebar, setIsOpenEditTargetSidebar] =
    useState<boolean>(false);
  const [isOpenAddFilterSidebar, setIsOpenAddFilterSidebar] =
    useState<boolean>(false);
  // State to track the row being deleted
  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isEditablecenter, setIsEditableCenter] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string | number>("");
  const selector = useSelector((state: RootState) => state?.user?.filter);

  const { permissions } = usePermission();

  const { canRead, canWrite, canUpdate, canDelete } =
    permissions[acl.configuration.module]?.targets;

  const {
    handleSubmit: handleAddTargetFormSubmit,
    control: addTargetFormControl,
    reset: resetTargetForm,
    getValues: getTargetValue,
    setValue: setTargetValue,
  } = useForm({
    defaultValues: {
      createNewTargetFormData: {
        center: { id: null, label: null },
        targetDuration: null,
        bonusPoints: null,
        malPractice: false,
        programmePerformance: {},
        qualityAndEffiancy: {},
      },
    },
  });

  const [params, setParams] = useState<any>({
    pageNo: "1",
    pageSize: "10",
    programMasterId: selector?.selectedProgram?.value,
    quarterMasterId: selector?.selectedQuarter?.quarterMasterId,
    ProgramYearId: selector?.selectedYear?.value,
  });

  const {
    handleSubmit: handleFilterSubmit,
    control: filterFormControl,
    reset: resetFilter,
  } = useForm({
    defaultValues: {
      filter: {
        targetDuration: [],
      },
    },
  });

  const toggleAddNewTargetSidebar = () => {
    resetTargetForm();
    setIsEditableCenter(false);
    setIsOpenAddTargetSidebar((preState) => !preState);
  };
  const toggleAddFilterSidebar = () =>
    setIsOpenAddFilterSidebar((preState) => !preState);

  const toggleEditTargetSidebar = (rowData: any) => {
    handleEditTargetFun(rowData);
    setIsOpenEditTargetSidebar((preState) => !preState);
  };

  const toggleDeleteModal = () =>
    setIsDeleteModalOpen((prevState) => !prevState);

  // all api call

  const {
    data: targetData,
    isLoading: isGettingTarget,
    error,
    refetch: refetchTarget,
  } = useGetTargetQuery(params);

  const { data: centersOptions = [], isLoading: isLoadingCenterManager } =
    useCenterListQuery({
      pageNo: "1",
      pageSize:"10000"
    }, { refetchOnMountOrArgChange: true } );

  const {
    data: targetParameterData,
    isLoading: parameterGetting,
    refetch: targetParameterRefetch,
  } = useGetTargetParameterQuery({
    programMasterId: selector?.selectedProgram?.value || undefined,
  });

  const [
    createTarget,
    { data: createdTargetResponse, isLoading: isTargetCreating },
  ] = useCreateTargetMutation();

  const [
    deleteTarget,
    { data: deleteTargetResponse, isLoading: isTargetDeleting },
  ] = useDeleteTargetMutation();

  const handleDelete = (row: any) => {
    toggleDeleteModal();
    setDeletingRowId(row.targetId);
  };

  const onDelete = async () => {
    try {
      const res = await deleteTarget(deletingRowId);
      if (res?.data?.success) {
        showToast(
          res?.data?.message || "Target deleted successfully",
          "success"
        );
        if (targetData?.length === 1) {
          setParams((prevParams: any) => ({
            ...prevParams,
            pageSize: "10",
            pageNo: Math.max(prevParams.pageNo - 1, 1).toString(),
          }));
        }
      }
    } catch (error) {
      console.error(error);
    }
    toggleDeleteModal();
  };

  const handleEditTargetFun = (targetRowData: any) => {
    const {
      centerId,
      centerName,
      targetDuration,
      targetScores,
      bonusPoints,
      malPractice,
    } = targetRowData;
    setIsEditableCenter(true);

    // Initialize an empty object to hold the data for each category
    const initialData: Record<string, any> = {};

    Object.entries(targetParameterData || {}).forEach(
      ([category, parameters]: any) => {
        initialData[category] = {};
        Object.entries(parameters).forEach(
          ([parameterName, parameterList]: any) => {
            parameterList.forEach((parameter: any) => {
              initialData[category][parameter.id] = undefined;
            });
          }
        );
      }
    );

    // Fill in the target scores into their respective categories
    // console.log(initialProgrammePerformance, initialQualityAndEfficacy);

    const transformedData = Object.keys(
      initialData["Programme performance"]
    )?.reduce((acc: any, category: any) => {
      targetParameterData[category]?.forEach((item: any) => {
        acc[item.id] = item.name;
      });
      return acc;
    }, {});

    //   // Dynamically create data objects based on the structure you want
    const programmePerformanceData = initialData["Programme performance"];

    const qualityAndEfficacyData = initialData["Quality and efficacy"];

    targetScores?.forEach((scoreData: any) => {
      const { targetParameterId, score } = scoreData;

      if (targetParameterId in programmePerformanceData) {
        programmePerformanceData[
          targetParameterId as keyof typeof programmePerformanceData
        ] = score || 0;
      }

      if (targetParameterId in qualityAndEfficacyData) {
        qualityAndEfficacyData[
          targetParameterId as keyof typeof qualityAndEfficacyData
        ] = score || 0;
      }
    });

    // Prefill the form
    resetTargetForm({
      createNewTargetFormData: {
        center: { id: centerId, label: centerName },
        targetDuration: targetDuration,
        bonusPoints: bonusPoints || 0,
        malPractice: malPractice || false,
        programmePerformance: programmePerformanceData,
        qualityAndEffiancy: qualityAndEfficacyData,
      },
    });
  };

  const handleEditTargetDoneBtn = handleAddTargetFormSubmit(
    async (data: any) => {
      const payload = {
        centerId: data?.createNewTargetFormData?.center.id,
        programMasterId: selector?.selectedProgram?.value || undefined,
        quarterMasterId:
          selector?.selectedQuarter?.quarterMasterId || undefined,
        ProgramYearId: selector?.selectedYear?.value || undefined,
        targetDuration: data?.createNewTargetFormData?.targetDuration,
        bonusPoints: data?.createNewTargetFormData?.bonusPoints || 0,
        malPractice: data?.createNewTargetFormData?.malPractice || false,
        target: [
          {
            parameterTypeId: 1, // Programme Performance
            targetScore: Object.keys(
              data?.createNewTargetFormData?.programmePerformance || {}
            ).map((key) => ({
              targetParameterId: key,
              score: parseInt(
                data?.createNewTargetFormData?.programmePerformance[key]
              ),
            })),
          },
          {
            parameterTypeId: 2, // Quality and Efficiency
            targetScore: Object.keys(
              data?.createNewTargetFormData?.qualityAndEffiancy || {}
            ).map((key) => ({
              targetParameterId: key,
              score: parseInt(
                data?.createNewTargetFormData?.qualityAndEffiancy[key]
              ),
            })),
          },
        ],
      };

      try {
        const res = await createTarget(payload);
        if (res?.data?.success) {
          showToast("Target updated successfully", "success");
        }
      } catch (error) {
        console.error(error);
      }
      setIsOpenEditTargetSidebar((preState) => !preState);
      resetTargetForm();
    }
  );

  const handleAddTargetDoneBtn = handleAddTargetFormSubmit(
    async (data: any) => {
      const payload = {
        centerId: data?.createNewTargetFormData?.center.id,
        programMasterId: selector?.selectedProgram?.value || undefined,
        quarterMasterId:
          selector?.selectedQuarter?.quarterMasterId || undefined,
        ProgramYearId: selector?.selectedYear?.value || undefined,
        targetDuration: data?.createNewTargetFormData?.targetDuration,
        bonusPoints: data?.createNewTargetFormData?.bonusPoints || 0,
        malPractice: data?.createNewTargetFormData?.malPractice || false,
        target: [
          {
            parameterTypeId: 1, // Programme Performance
            targetScore: Object.keys(
              data?.createNewTargetFormData?.programmePerformance || {}
            ).map((key) => ({
              targetParameterId: key,
              score: data?.createNewTargetFormData?.programmePerformance[key],
            })),
          },
          {
            parameterTypeId: 2, // Quality and Efficiency
            targetScore: Object.keys(
              data?.createNewTargetFormData?.qualityAndEffiancy || {}
            ).map((key) => ({
              targetParameterId: key,
              score: data?.createNewTargetFormData?.qualityAndEffiancy[key],
            })),
          },
        ],
      };

      try {
        const res = await createTarget(payload);
        if (res?.data?.success) {
          showToast("Target created successfully", "success");
        }
      } catch (error) {
        console.error(error);
      }
      setIsOpenAddTargetSidebar((preState) => !preState);
    }
  );

  const onCloseCreateNewTargetSidebar = () => {
    resetTargetForm();
    toggleAddNewTargetSidebar();
  };
  const onCloseEditTargetSidebar = () => {
    setIsOpenEditTargetSidebar((preState) => !preState);
  };

  const onCloseFilterSidebar = () => {
    toggleAddFilterSidebar();
    resetFilter();
  };

  const handleClearFilter = () => {
    setParams((prevParams: any) => ({
      ...prevParams,
      targetDuration: undefined,
    }));

    onCloseFilterSidebar();
  };

  const handleAddFilterDoneBtn = handleFilterSubmit(async (data) => {
    if (data?.filter?.targetDuration.length >= 2) {
      return handleClearFilter();
    }
    setParams((prevParams: any) => ({
      ...prevParams,
      targetDuration: data?.filter?.targetDuration.join(",") || undefined,
    }));
    setIsOpenAddFilterSidebar((preState) => !preState);
  });

  const handleSearch = (value: string | number) => {
    setSearchTerm(value);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setParams((prevParams: any) => ({
        ...prevParams,
        search: searchTerm ? searchTerm : undefined,
      }));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    setParams({
      pageNo: "1",
      pageSize: "10",
      programMasterId: selector?.selectedProgram?.value,
      quarterMasterId: selector?.selectedQuarter?.quarterMasterId,
      ProgramYearId: selector?.selectedYear?.value,
    });

    targetParameterRefetch();
  }, [selector]);

  const onPageChange = async (event: any, page: number) => {
    setParams((prevParams: any) => ({
      ...prevParams,
      pageNo: page.toString(),
      pageSize: "10",
    }));
  };

  const headerBtn = [
    {
      text: "New target",
      variant: "contained",
      startIcon: <AddIcon />,
      onClick: toggleAddNewTargetSidebar,
      sx: {
        border: "1px solid #a18218",
        backgroundColor: "#fff1b7",
        "&:hover": {
          backgroundColor: "#e8d78e",
        },
        fontWeight: 600,
        boxShadow: "none !important",
        borderRadius: "8px",
      },
    },
  ];
  const tableHeaderBtn = [
    {
      text: "Add Filter",
      variant: "contained",
      startIcon: <FilterListRoundedIcon />,
      onClick: toggleAddFilterSidebar,
      sx: {
        border: "1px solid #8D8D8D",
        backgroundColor: "#fff",
        "&:hover": {
          backgroundColor: "#ededed",
        },
        fontWeight: 600,
        boxShadow: "none !important",
        borderRadius: "8px",
      },
    },
  ];

  const generateColumns = (data: Record<string, any> = {}) => {
    const staticColumns = [
      {
        field: "centerName",
        headerName: "Centre",
        minWidth: 230,
      },
      {
        field: "targetDuration",
        headerName: "Target Duration",
        minWidth: 150,
        renderCell: (row: any) => row?.value || " - ",
      },
    ];

    const dynamicColumns = Object.entries(data || {}).flatMap(
      ([groupName, categories]) =>
        Object.entries(categories || {}).flatMap(
          ([categoryName, parameters]: any) =>
            (parameters || []).map((parameter: any) => ({
              field: parameter.id,
              headerName: parameter.name,
              minWidth: 150,
              flex: "auto",
              renderCell: (row: any) => {
                const score = row.row.targetScores?.find(
                  (scoreItem: any) =>
                    scoreItem?.targetParameterId === parameter.id
                )?.score;

                const scoreAchived = row.row.targetScores?.find(
                  (scoreItem: any) =>
                    scoreItem?.targetParameterId === parameter.id
                )?.scoreAchived;
                return score ? `${score} / ${scoreAchived}` : `0 / ${scoreAchived || 0}`;
              },
            }))
        )
    );

    const actionBtn = [
      {
        field: "action",
        headerName: "Action",
        flex: 0,
        minWidth: 170,
        sortable: false,
        renderCell: (props: { row: any }) => (
          <>
            <Button
              onClick={() => toggleEditTargetSidebar(props.row)}
              variant="contained"
              disabled={!canUpdate}
              sx={{
                textTransform: "capitalize",
                border: "1px solid #997906",
                borderRadius: "8px",
                backgroundColor: "#FFF1B7",
                color: "black",
                marginRight: "10px",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
            >
              Edit
            </Button>
            <Button
              onClick={() => handleDelete(props.row)}
              variant="contained"
              disabled={!canUpdate}
              sx={{
                textTransform: "capitalize",
                border: "1px solid #997906",
                borderRadius: "8px",
                backgroundColor: "#FFF1B7",
                padding: "8.2px 0px",
                margin: "10px",
                color: "black",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
            >
              <ImageComponent
                src={"/assets/images/login/Delete.svg"}
                sx={{ cursor: "pointer", height: "20px", width: "22px" }}
              />
            </Button>
          </>
        ),
      },
    ];

    return [...staticColumns, ...dynamicColumns, ...actionBtn];
  };

  const columns = generateColumns(targetParameterData);


  const totalRowCount = targetData?.totalCount || 0;
  const totalPages = Number(Math.ceil(Number(totalRowCount) / 10));

  return (
    <>
      <Modal open={isDeleteModalOpen} disableClose={true}>
        <>
          <Box
            padding={1}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
              flexDirection: "column",
            }}
          >
            <Box height={"50px"}>
              <ImageComponent src="/assets/images/icon/warning.svg" />
            </Box>
            <Text
              sx={{ color: "#101828", fontWeight: "600", fontSize: "18px" }}
            >
              Are you sure you want to delete this?
            </Text>
            <Text
              sx={{
                color: "#667085",
                fontSize: "13px",
                fontWeight: "400",
                textAlign: "center",
              }}
            >
              By clicking on the Delete button you are making sure that you want
              to delete this permanently.
            </Text>

            <Stack flexDirection={"row"} width={"100%"} gap="15px">
              <Button
                variant="contained"
                sx={{
                  flex: 1,
                  borderRadius: "8px",
                  border: "1px solid #8D8D8D",
                  backgroundColor: "#fff",
                  boxShadow: "none",
                  textTransform: "capitalize",
                  fontWeight: "600",
                  "&:hover": {
                    backgroundColor: "#ededed",
                    boxShadow: "none",
                  },
                }}
                onClick={toggleDeleteModal}
              >
                No
              </Button>
              <Button
                sx={{
                  flex: 1,
                  borderRadius: "8px",
                  border: "1px solid #a18218",
                  backgroundColor: "#fef7da",
                  boxShadow: "none",
                  textTransform: "capitalize",
                  fontWeight: "600",
                  "&:hover": {
                    backgroundColor: "#e8d78e",
                    boxShadow: "none",
                  },
                }}
                loading={isTargetDeleting}
                onClick={onDelete}
              >
                delete
              </Button>
            </Stack>
          </Box>
        </>
      </Modal>

      <PageHeader
        title="All Targets"
        subTitle="Here you can view the targets given to the employees."
        chipLabel={`${totalRowCount} targets`}
        buttons={canWrite ? headerBtn : []}
      />

      <TableHeader
        buttons={tableHeaderBtn}
        onSearchfieldChange={handleSearch}
      />

      <Table
        columns={columns}
        rows={targetData?.targets}
        rowCellsSx={{ paddingLeft: "10px", textAlign: "start" }}
        headerSx={{
          textAlign: "start",
          fontWeight: 600,
          color: "#242424",
          padding: "13px 0px 13px 10px",
        }}
        tableSx={{ minHeight: "247px" }}
        handlePageChange={onPageChange}
        count={totalPages}
        loading={isGettingTarget}
        getRowId={(row: any) => row.id}
        currentPage={Number(params?.pageNo) || 1}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#DADADA",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
        }}
      />

      <Sidebar
        title="Create New target"
        subtitle="Here you can create new target by filling the details."
        drawerOpen={isOpenAddTargetSidebar}
        onDrawerToggle={toggleAddNewTargetSidebar}
        sx={{ width: "450px" }}
        actionComponent={() => (
          <>
            <Button
              className="bun-outline"
              onClick={onCloseCreateNewTargetSidebar}
            >
              Cancel
            </Button>
            <Button
              className="bun-filled"
              onClick={handleAddTargetDoneBtn}
              loading={isTargetCreating}
            >
              {isTargetCreating ? "loading" : "Done"}
            </Button>
          </>
        )}
      >
        <AddTargetForm
          control={addTargetFormControl}
          centerOptions={centersOptions}
          targetParameterData={targetParameterData}
          isEditablecenter={isEditablecenter}
        />
      </Sidebar>

      <Sidebar
        title="Edit Target"
        subtitle="Here you can edit target by filling the details."
        drawerOpen={isOpenEditTargetSidebar}
        onDrawerToggle={toggleEditTargetSidebar}
        sx={{ width: "450px" }}
        actionComponent={() => (
          <>
            <Button className="bun-outline" onClick={onCloseEditTargetSidebar}>
              Cancel
            </Button>
            <Button
              className="bun-filled"
              onClick={handleEditTargetDoneBtn}
              loading={isTargetCreating}
            >
              {isTargetCreating ? "loading" : "Done"}
            </Button>
          </>
        )}
      >
        <AddTargetForm
          control={addTargetFormControl}
          centerOptions={centersOptions}
          targetParameterData={targetParameterData}
          isEditablecenter={isEditablecenter}
        />
      </Sidebar>

      <Sidebar
        title="Add Filters"
        subtitle="See the data in an organized manner by applying filters"
        onDrawerToggle={toggleAddFilterSidebar}
        drawerOpen={isOpenAddFilterSidebar}
        actionComponent={() => (
          <>
            <Button className="bun-outline" onClick={handleClearFilter}>
              Clear all
            </Button>
            <Button className="bun-filled" onClick={handleAddFilterDoneBtn}>
              Apply
            </Button>
          </>
        )}
      >
        <FilterTarget controller={filterFormControl} />
      </Sidebar>
    </>
  );
};

export default Targets;
